import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { EmptyStateMessage } from "../EmptyStateMessage";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { DataTablePagination, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "./DataTablePagination";
import { DataTableToolbar } from "./DataTableToolbar";

const CELL_PADDING = {
  md: { xs: "8px 6px", md: "12px 16px" },
  sm: { xs: "6px 6px", md: "8px 12px" },
};

const HEADER_PADDING = {
  md: { xs: "10px 6px", md: "12px 16px" },
  sm: { xs: "8px 6px", md: "10px 12px" },
};

/**
 * Tabla única de la app. Una sola "card" con:
 *  - toolbar integrado (tabs, buscador, ordenar, filtros, chips, slots libres)
 *  - tabla MUI con cabecera fija opcional, skeleton de carga y estado vacío
 *  - paginación común (base 0) o un `footer` libre (p. ej. "cargar más")
 *
 * Todo lo del toolbar es opcional: solo se pinta lo que se pasa por props, y si no
 * hay nada que pintar el toolbar no aparece.
 *
 * columns: [{ key, header, tooltip?, align?, render?(row, index), width?, minWidth?,
 *             headerSx?, cellSx?, hidden? }]
 *
 * stickyFirstColumn: fija la primera columna (con una línea vertical) y deja que solo
 * el resto haga scroll horizontal. Funciona también con `renderRow`.
 */
export function DataTable({
  columns,
  rows,
  getRowId,
  loading = false,
  skeletonRows = 5,
  emptyMessage = "No hay datos disponibles",
  emptyIcon,
  emptyContent,
  onRowClick,
  isRowClickable,
  getRowSx,
  getRowProps,
  renderRow,
  size = "md",
  tableMinWidth,
  stickyFirstColumn = false,
  maxHeight,
  minHeight,
  stickyHeader,
  variant = "card",
  sx,
  tableSx,
  // Toolbar
  tabs,
  activeTab,
  onTabChange,
  tabsDisabled = false,
  showSearch = true,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Buscar...",
  sortFieldOptions,
  sortField,
  onSortFieldChange,
  sortDirection = "desc",
  onSortDirectionChange,
  sortDirectionOptions = [],
  showSortDirection = true,
  onFilterClick,
  activeFiltersCount = 0,
  filterTooltip = "Filtrar",
  filterChips,
  toolbarStart,
  toolbarEnd,
  // Paginación
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  hidePaginationWhenEmpty = true,
  footer,
  paginationStart, // content at the left of the pagination (e.g. a secondary action)
  slotProps, // { toolbar, tableContainer }: props extra (p. ej. data-tutorial)
}) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();

  const visibleColumns = React.useMemo(
    () => columns.filter((column) => !column.hidden),
    [columns]
  );

  const safeRows = Array.isArray(rows) ? rows : [];
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const hasToolbar =
    hasTabs ||
    Boolean(showSearch && onSearchChange) ||
    Boolean(sortFieldOptions?.length && onSortFieldChange) ||
    Boolean(onFilterClick) ||
    Boolean(filterChips?.length) ||
    Boolean(toolbarStart) ||
    Boolean(toolbarEnd);

  const hasPagination = typeof onPageChange === "function" && typeof page === "number";
  const showPagination =
    hasPagination && !(hidePaginationWhenEmpty && (loading || safeRows.length === 0));
  const isSticky = stickyHeader ?? Boolean(maxHeight);

  const resolveRowId = (row, index) => (getRowId ? getRowId(row, index) : row?.id ?? index);
  const alignFor = (column) => column.align || "left";

  const renderBody = () => {
    if (loading) {
      return Array.from({ length: skeletonRows }).map((_, rowIndex) => (
        <TableRow key={`data-table-skeleton-${rowIndex}`}>
          {visibleColumns.map((column, colIndex) => (
            <TableCell key={column.key} align={alignFor(column)}>
              <Skeleton
                variant="text"
                width={colIndex === 0 ? "80%" : 90}
                height={16}
                sx={column.align === "right" ? { ml: "auto" } : undefined}
              />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (safeRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={visibleColumns.length} sx={{ borderBottom: "none" }}>
            {emptyContent ?? (
              <Box sx={{ py: 6 }}>
                <EmptyStateMessage icon={emptyIcon} message={emptyMessage} />
              </Box>
            )}
          </TableCell>
        </TableRow>
      );
    }

    return safeRows.map((row, rowIndex) => {
      if (renderRow) {
        return (
          <React.Fragment key={resolveRowId(row, rowIndex)}>
            {renderRow(row, rowIndex, visibleColumns)}
          </React.Fragment>
        );
      }

      const clickable = onRowClick
        ? isRowClickable
          ? isRowClickable(row, rowIndex)
          : true
        : false;

      return (
        <TableRow
          {...(getRowProps ? getRowProps(row, rowIndex) : {})}
          key={resolveRowId(row, rowIndex)}
          hover
          onClick={clickable ? (event) => onRowClick(row, rowIndex, event) : undefined}
          sx={{
            cursor: clickable ? "pointer" : "default",
            ...(getRowSx ? getRowSx(row, rowIndex) : {}),
          }}
        >
          {visibleColumns.map((column) => (
            <TableCell
              key={column.key}
              align={alignFor(column)}
              sx={{
                ...(column.width !== undefined && { width: column.width }),
                ...(column.minWidth !== undefined && { minWidth: column.minWidth }),
                ...(typeof column.cellSx === "function"
                  ? column.cellSx(row, rowIndex)
                  : column.cellSx),
              }}
            >
              {column.render ? column.render(row, rowIndex) : row?.[column.key] ?? "-"}
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  const isCard = variant === "card";

  // Se aplica por CSS (no por columna) para cubrir también las filas de `renderRow`.
  // La línea es un box-shadow porque con border-collapse el borde de una celda
  // sticky se queda atrás al hacer scroll.
  const stickyFirstColumnSx = stickyFirstColumn
    ? {
        "& .MuiTableRow-root > .MuiTableCell-root:first-of-type:not([colspan])": {
          position: "sticky",
          left: 0,
          zIndex: 1,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `inset -1px 0 0 ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root > .MuiTableCell-root:first-of-type": {
          zIndex: 3,
          backgroundColor: neutrals.gray[50],
        },
        // Mantener el color de hover de la fila también en la celda fija
        "& .MuiTableRow-hover:hover > .MuiTableCell-root:first-of-type:not([colspan])": {
          backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
        },
      }
    : {};

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        ...(isCard && {
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
        }),
        ...sx,
      }}
    >
      {hasToolbar ? (
        <DataTableToolbar
          {...slotProps?.toolbar}
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
          tabsDisabled={tabsDisabled}
          showSearch={showSearch}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          sortFieldOptions={sortFieldOptions}
          sortField={sortField}
          onSortFieldChange={onSortFieldChange}
          sortDirection={sortDirection}
          onSortDirectionChange={onSortDirectionChange}
          sortDirectionOptions={sortDirectionOptions}
          showSortDirection={showSortDirection}
          onFilterClick={onFilterClick}
          activeFiltersCount={activeFiltersCount}
          filterTooltip={filterTooltip}
          filterChips={filterChips}
          toolbarStart={toolbarStart}
          toolbarEnd={toolbarEnd}
        />
      ) : null}

      <TableContainer
        {...slotProps?.tableContainer}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: maxHeight ? "auto" : "visible",
          ...(maxHeight && { maxHeight }),
          ...(minHeight && { minHeight }),
          "&::-webkit-scrollbar": { height: "8px", width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: neutrals.gray[100] },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: neutrals.gray[300],
            borderRadius: "4px",
            "&:hover": { backgroundColor: neutrals.gray[400] },
          },
        }}
      >
        <Table
          stickyHeader={isSticky}
          sx={{
            width: "100%",
            tableLayout: "auto",
            ...(tableMinWidth && { minWidth: tableMinWidth }),
            "& .MuiTableCell-root": {
              whiteSpace: "nowrap",
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              padding: CELL_PADDING[size] || CELL_PADDING.md,
              verticalAlign: "middle",
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
              borderBottom: "none",
            },
            ...stickyFirstColumnSx,
            ...tableSx,
          }}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => {
                const header = column.tooltip ? (
                  <Tooltip title={column.tooltip} arrow>
                    <span>{column.header}</span>
                  </Tooltip>
                ) : (
                  column.header
                );

                return (
                  <TableCell
                    key={column.key}
                    align={alignFor(column)}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      backgroundColor: neutrals.gray[50],
                      padding: HEADER_PADDING[size] || HEADER_PADDING.md,
                      ...(column.width !== undefined && { width: column.width }),
                      ...(column.minWidth !== undefined && { minWidth: column.minWidth }),
                      ...column.headerSx,
                    }}
                  >
                    {header}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableContainer>

      {showPagination && paginationStart ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: { xs: 1, md: 2 },
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{paginationStart}</Box>
          <DataTablePagination
            count={typeof totalCount === "number" ? totalCount : safeRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={rowsPerPageOptions}
            sx={{ borderTop: "none", flex: 1, minWidth: 0 }}
          />
        </Box>
      ) : showPagination ? (
        <DataTablePagination
          count={typeof totalCount === "number" ? totalCount : safeRows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      ) : (
        paginationStart && (
          <Box sx={{ display: "flex", gap: 1, px: 2, py: 1.25, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            {paginationStart}
          </Box>
        )
      )}

      {footer}
    </Box>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.node,
      tooltip: PropTypes.node,
      align: PropTypes.oneOf(["left", "center", "right"]),
      render: PropTypes.func,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
      headerSx: PropTypes.object,
      cellSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
      hidden: PropTypes.bool,
    })
  ).isRequired,
  rows: PropTypes.array,
  getRowId: PropTypes.func,
  loading: PropTypes.bool,
  skeletonRows: PropTypes.number,
  emptyMessage: PropTypes.string,
  emptyIcon: PropTypes.elementType,
  emptyContent: PropTypes.node,
  onRowClick: PropTypes.func,
  isRowClickable: PropTypes.func,
  getRowSx: PropTypes.func,
  getRowProps: PropTypes.func,
  renderRow: PropTypes.func,
  size: PropTypes.oneOf(["sm", "md"]),
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  stickyFirstColumn: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  stickyHeader: PropTypes.bool,
  variant: PropTypes.oneOf(["card", "plain"]),
  sx: PropTypes.object,
  tableSx: PropTypes.object,
  tabs: PropTypes.array,
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTabChange: PropTypes.func,
  tabsDisabled: PropTypes.bool,
  showSearch: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  sortFieldOptions: PropTypes.array,
  sortField: PropTypes.string,
  onSortFieldChange: PropTypes.func,
  sortDirection: PropTypes.oneOf(["asc", "desc"]),
  onSortDirectionChange: PropTypes.func,
  sortDirectionOptions: PropTypes.array,
  showSortDirection: PropTypes.bool,
  onFilterClick: PropTypes.func,
  activeFiltersCount: PropTypes.number,
  filterTooltip: PropTypes.string,
  filterChips: PropTypes.array,
  toolbarStart: PropTypes.node,
  toolbarEnd: PropTypes.node,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  slotProps: PropTypes.shape({
    toolbar: PropTypes.object,
    tableContainer: PropTypes.object,
  }),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  hidePaginationWhenEmpty: PropTypes.bool,
  footer: PropTypes.node,
  paginationStart: PropTypes.node,
};

export default DataTable;
